import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  AuthSession,
  createGuestSession,
  fetchAuthSession,
  loginWithEmail,
  logoutSession,
  registerWithEmail,
  resendVerificationEmail,
  verifyEmailWithCode,
} from './authApi';
import { clearSentryUser, setSentryUser } from '../common/sentry';
import { clearGameViewedCache } from '../replay/gameViewedCache';
import { markTrialWelcomePending } from '../subscription/trialWelcomeStorage';
import {
  consumeGuestMergedQueryParam,
  notifyGuestDataMergedIfPresent,
} from './guestMergeToast';
import { markLoggedInBefore } from './priorLoginCookie';
import { useRecordDeviceSettings } from '../deviceSettings/useRecordDeviceSettings';

interface AuthContextProps {
  user: AuthSession | null;
  isLoggedIn: boolean;
  isGuest: boolean;
  authChecked: boolean;
  loginWithGoogle: () => void;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  registerWithPassword: (email: string, password: string) => Promise<string | undefined>;
  resendVerification: () => Promise<void>;
  verifyEmailCode: (code: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  continueAsGuest: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthSession | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const sessionOpRef = useRef(0);

  const establishSession = useCallback((session: AuthSession) => {
    sessionOpRef.current += 1;
    if (!session.isGuest && session.provider !== 'guest') {
      markLoggedInBefore();
    }
    setUser(session);
    setAuthChecked(true);
  }, []);

  const refreshSession = useCallback(async () => {
    const op = ++sessionOpRef.current;
    try {
      const session = await fetchAuthSession();
      if (op !== sessionOpRef.current) {
        return;
      }
      if (session) {
        if (!session.isGuest && session.provider !== 'guest') {
          markLoggedInBefore();
        }
        setUser(session);
      } else {
        // First-time visitors stay anonymous on public catalog pages until they
        // enter a private surface (guard provisions a guest) or start replay.
        setUser(null);
      }
    } catch {
      if (op !== sessionOpRef.current) {
        return;
      }
      setUser(null);
    } finally {
      if (op === sessionOpRef.current) {
        setAuthChecked(true);
      }
    }
  }, []);

  const continueAsGuest = useCallback(async () => {
    establishSession(await createGuestSession());
  }, [establishSession]);

  useEffect(() => {
    consumeGuestMergedQueryParam();
    void refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    if (user?.id) {
      setSentryUser(user.id);
    } else {
      clearSentryUser();
    }
  }, [user?.id]);

  useEffect(() => {
    clearGameViewedCache();
  }, [user?.id]);

  useRecordDeviceSettings(user?.id, authChecked);

  const loginWithGoogle = () => {
    markTrialWelcomePending();
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
  };

  const loginWithPassword = async (email: string, password: string) => {
    const session = await loginWithEmail(email, password);
    markTrialWelcomePending();
    notifyGuestDataMergedIfPresent(session.mergedGuestData);
    establishSession(session);
  };

  const registerWithPassword = async (email: string, password: string) => {
    const result = await registerWithEmail(email, password);
    markTrialWelcomePending();
    notifyGuestDataMergedIfPresent(result.mergedGuestData);
    establishSession(result);
    return result.message;
  };

  const resendVerification = async () => {
    await resendVerificationEmail();
  };

  const verifyEmailCode = useCallback(async (code: string) => {
    await verifyEmailWithCode(code);
    markTrialWelcomePending();
    await refreshSession();
  }, [refreshSession]);

  const logout = async () => {
    await logoutSession();
    await refreshSession();
  };

  const isGuest = user?.isGuest === true || user?.provider === 'guest';

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn: user != null,
        isGuest,
        authChecked,
        loginWithGoogle,
        loginWithPassword,
        registerWithPassword,
        resendVerification,
        verifyEmailCode,
        refreshSession,
        continueAsGuest,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
